/**
 * Renders a gallery for selecting a component out of the component library.
 * Shows a preview using the default props defined in the schema of each widget.
 * @flow
 */
import React from 'react';
import injectSheet from 'react-jss';
import classNames from 'classnames';
import Modal from '~/app/components/Modal';
import Button from '~/app/ui/Button';
import Widgets from 'shared/widgets';
import widgetSchemas from 'shared/widgets/schemas';
import ErrorBoundary from 'shared/components/Error/ErrorBoundary';
import { A, DARK } from '~/app/ui/tokens';
import { buildWidgetSrcDoc } from '~/widgets/components/widgetPreviewDoc';

type $ComponentData = {
  widgetId: string,
  props: Object
}

type Props = {
  classes: Object,
  onSelect: ($ComponentData) => void,
  onClose: Function
};

/**
 * Fetch the lightweight custom-widget list (metadata only — no html/css/js
 * bodies). The body of a widget is loaded on demand when its preview is shown
 * (see fetchCustomWidgetBody). This keeps opening the browser cheap even with
 * hundreds of custom widgets.
 */
export async function fetchCustomWidgets() {
  try {
    const response = await fetch('/graphql', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        query: `
          query GetCustomWidgetSummaries {
            customWidgetSummaries(isActive: true) {
              _id
              name
              title
              description
              category
              publishedVersion
            }
          }
        `
      })
    });

    const result = await response.json();

    if (result.errors) {
      console.error('Error fetching custom widgets:', result.errors);
      return [];
    }

    return result.data?.customWidgetSummaries || [];
  } catch (error) {
    console.error('Failed to fetch custom widgets:', error);
    return [];
  }
}

/**
 * Cache of custom-widget bodies (html/css/js/propsSchema/defaultProps), keyed by
 * name + version. Module-level so it survives reopening the browser. Stores the
 * in-flight Promise so concurrent callers (preview render + Select) share one
 * request. Failed/empty lookups are evicted so they can be retried.
 */
const customBodyCache = new Map();

export function fetchCustomWidgetBody(name, version) {
  const key = `${name}@@${version || ''}`;
  if (customBodyCache.has(key)) {
    return customBodyCache.get(key);
  }

  const promise = fetch('/graphql', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      query: `
        query GetCustomWidgetForRender($name: String!, $version: String) {
          customWidgetForRender(name: $name, version: $version) {
            html
            css
            js
            propsSchema {
              key
              label
              type
              defaultValue
              children {
                key
                label
                type
                defaultValue
              }
            }
            defaultProps
            scoper
          }
        }
      `,
      variables: { name, version: version || undefined }
    })
  })
    .then(res => res.json())
    .then((result) => {
      if (result.errors) {
        console.error('Error fetching custom widget body:', result.errors);
        customBodyCache.delete(key);
        return null;
      }
      const body = result.data?.customWidgetForRender || null;
      if (!body) {
        customBodyCache.delete(key);
      }
      return body;
    })
    .catch((error) => {
      console.error('Failed to fetch custom widget body:', error);
      customBodyCache.delete(key);
      return null;
    });

  customBodyCache.set(key, promise);
  return promise;
}

/**
 * Lazy-loaded preview component for custom widgets
 * Only renders the iframe after a short delay to improve initial render performance
 * Ensures loading animation is visible for at least 1 second for better UX
 */
const LazyPreviewIframe = React.memo(({ srcDoc, title }) => {
  const [shouldLoad, setShouldLoad] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(true);
  const startTimeRef = React.useRef(Date.now());
  const loadTimeRef = React.useRef(null);
  
  React.useEffect(() => {
    // Defer iframe loading slightly to allow React to render the placeholder first
    const timer = setTimeout(() => {
      setShouldLoad(true);
    }, 50);
    
    return () => clearTimeout(timer);
  }, []);
  
  const handleLoad = React.useCallback(() => {
    const now = Date.now();
    loadTimeRef.current = now;
    const elapsed = now - startTimeRef.current;
    const minDisplayTime = 500; // 0.5 second minimum
    
    if (elapsed >= minDisplayTime) {
      // Already been at least 0.5 seconds, hide loading immediately
      setIsLoading(false);
    } else {
      // Wait until 0.5 seconds have passed before hiding loading
      const remainingTime = minDisplayTime - elapsed;
      setTimeout(() => {
        setIsLoading(false);
      }, remainingTime);
    }
  }, []);
  
  const containerStyle = {
    width: '100%',
    height: '100%',
    background: '#f8fafc',
    borderRadius: 8,
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    position: 'relative'
  };
  
  const iframeStyle = {
    width: '100%',
    height: '100%',
    minHeight: '90vh',
    border: 'none',
    display: 'block',
    flex: 1,
    opacity: isLoading ? 0 : 1,
    transition: 'opacity 200ms ease-in'
  };
  
  const loadingStyle = {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#f8fafc',
    color: '#64748b',
    fontSize: 14,
    opacity: isLoading ? 1 : 0,
    pointerEvents: isLoading ? 'auto' : 'none',
    transition: 'opacity 200ms ease-out'
  };
  
  return (
    <div style={containerStyle}>
      {isLoading && (
        <div style={loadingStyle}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ 
              width: 40, 
              height: 40, 
              border: '3px solid #e2e8f0',
              borderTopColor: '#3b82f6',
              borderRadius: '50%',
              animation: 'spin 0.8s linear infinite',
              margin: '0 auto 12px'
            }} />
            <div>Loading preview...</div>
          </div>
        </div>
      )}
      {shouldLoad && (
        <iframe
          srcDoc={srcDoc}
          style={iframeStyle}
          title={title}
          sandbox="allow-scripts"
          onLoad={handleLoad}
        />
      )}
      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
});

LazyPreviewIframe.displayName = 'LazyPreviewIframe';

/**
 * Create a preview component for a custom widget from its metadata.
 *
 * The widget's body (html/css/js/defaultProps) is NOT included in the metadata;
 * it is fetched on demand (and cached) when this preview first mounts — i.e.
 * only when the user actually pages to this widget. The schema carries only the
 * identifying props the picker needs; the full insert payload is rebuilt from
 * the fetched body in ComponentBrowser.handleSelect.
 */
function createCustomWidgetComponent(meta) {
  // Schema that matches the standard widget format. defaultProps holds only the
  // identifiers needed to locate the widget; its real prop defaults come from the
  // body fetched at select time (see handleSelect).
  const schema = {
    id: 'CustomWidget',
    title: `${meta.title} (Custom)`,
    propsSchema: [
      {
        key: 'customWidgetName',
        label: 'Custom Widget',
        type: 'TEXT',
      },
      {
        key: 'customWidgetVersion',
        label: 'Version',
        type: 'TEXT',
      }
    ],
    defaultProps: {
      customWidgetName: meta.name,
      customWidgetVersion: meta.publishedVersion || '',
    },
    // Store original custom widget metadata for reference
    _customWidget: meta
  };

  // Create a simple preview component that loads its own body on demand.
  const PreviewComponent = React.memo(() => {
    const [body, setBody] = React.useState(null);
    const [status, setStatus] = React.useState('loading'); // 'loading' | 'ready' | 'empty'

    React.useEffect(() => {
      let alive = true;
      fetchCustomWidgetBody(meta.name, meta.publishedVersion).then((resolved) => {
        if (!alive) return;
        if (resolved && resolved.html) {
          setBody(resolved);
          setStatus('ready');
        } else {
          setStatus('empty');
        }
      });
      return () => { alive = false; };
    }, []);

    // Memoize srcDoc creation using useMemo to avoid recreating on every render
    // Must be called before any early returns to satisfy React hooks rules
    // The same document as the widget list's previews (escaping, v2 script wrapper, storage shim).
    const srcDoc = React.useMemo(() => (body && body.html ? buildWidgetSrcDoc(body, `preview-${meta.name}`, { padding: 16 }) : null), [body]);

    // While the body is loading, show a lightweight placeholder.
    if (status === 'loading') {
      return (
        <div style={{
          borderRadius: 8,
          padding: 24,
          textAlign: 'center',
          color: '#64748b',
          fontSize: 14,
          minHeight: '90vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          Loading preview…
        </div>
      );
    }

    // No published body available.
    if (status === 'empty' || !srcDoc) {
      return (
        <div style={{
          border: '1px dashed #cbd5e1',
          borderRadius: 8,
          padding: 24,
          textAlign: 'center',
          color: '#64748b',
          minHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <strong>{meta.title}</strong>
          <p style={{ margin: '8px 0 0 0', fontSize: 12 }}>
            {meta.description || 'Custom widget - no published version'}
          </p>
        </div>
      );
    }

    return (
      <LazyPreviewIframe
        srcDoc={srcDoc}
        title={meta.title}
      />
    );
  });

  PreviewComponent.displayName = 'PreviewComponent';

  PreviewComponent.schema = schema;

  return PreviewComponent;
}

class ComponentBrowser extends React.Component {
  props: Props = this.props;

  state = {
    indexCurrent: 0,
    filter: '',
    componentList: [],
    fullComponentList: [],
    customWidgets: []
  }

  componentDidMount() {
    // Built-in widgets: read metadata from the static schema manifest instead of
    // calling .load() on every code-split widget chunk (which downloaded ~126
    // bundles on open and blocked render). The actual component code for a widget
    // loads on demand only when its preview is shown — see render(): we mount the
    // loadable `entry.Component` for the currently visible widget only.
    // Each entry is shaped { schema, Component } so built-in and custom widgets
    // render through the same code path below.
    const builtInComponents = Object.keys(Widgets)
      .map(k => ({ schema: widgetSchemas[k], Component: Widgets[k] }))
      // Filter out the base CustomWidget - users should only see actual custom widgets
      .filter(entry => entry.schema && entry.schema.id !== 'CustomWidget');

    this.builtInComponents = builtInComponents;

    // Show built-in widgets immediately; the custom-widget list is metadata only
    // and appended once it arrives (each preview loads its own body on demand).
    this.setState({
      componentList: builtInComponents,
      fullComponentList: builtInComponents
    });

    fetchCustomWidgets().then((customWidgets) => {
      // Preview components for custom widgets that have a published version.
      const customComponents = customWidgets
        .filter(cw => cw.publishedVersion)
        .map((cw) => {
          const PreviewComponent = createCustomWidgetComponent(cw);
          return { schema: PreviewComponent.schema, Component: PreviewComponent, custom: cw };
        });

      // Built-in first, then custom widgets. Re-apply any active filter; keep the
      // current index since built-ins (shown first) don't shift when appending.
      const fullComponentList = [...this.builtInComponents, ...customComponents];

      this.setState(prev => ({
        fullComponentList,
        componentList: this.filterList(fullComponentList, prev.filter),
        customWidgets
      }));
    });

    document.addEventListener('keydown', this.handleKeyDown);
  }

  filterList = (list, filter) => (
    filter
      ? list.filter(c => c.schema.title.toUpperCase().includes(filter.toUpperCase()))
      : list
  );

  /**
   * Insert the selected widget. Rebuilds the exact insert payload the old
   * eager-fetch flow produced: built-ins use their manifest defaultProps; custom
   * widgets pull their published body's defaultProps (loaded/cached on demand).
   */
  handleSelect = async (entry) => {
    if (!entry) {
      return;
    }

    const { onSelect } = this.props;

    if (entry.custom) {
      const { name, publishedVersion } = entry.custom;
      const body = await fetchCustomWidgetBody(name, publishedVersion);
      const defaultProps = (body && body.defaultProps) || {};

      onSelect({
        presetId: null,
        widgetId: 'CustomWidget',
        props: {
          customWidgetName: name,
          // Empty = follow the latest published version (see customWidgetVersions.js).
          customWidgetVersion: '',
          widgetProps: defaultProps,
          ...defaultProps
        }
      });
      return;
    }

    onSelect({
      presetId: null,
      widgetId: entry.schema.id,
      props: entry.schema.defaultProps
    });
  }

  /**
   * Cycle through next/prev item.
   */
  handlePager = (direction) => {
    const { indexCurrent, componentList } = this.state;
    const max = componentList.length;

    if (direction === 'PREV') {
      const isFirst = indexCurrent === 0;
      const prev = isFirst ? (max - 1) : indexCurrent - 1;

      this.setState({ indexCurrent: prev });
    } else if (direction === 'NEXT') {
      const isLast = indexCurrent === (max - 1);
      const next = isLast ? 0 : (indexCurrent + 1);

      this.setState({ indexCurrent: next });
    }
  }

  handleKeyDown = (event) => {
    if (event.keyCode === 37) {
      this.handlePager('PREV');
    }

    if (event.keyCode === 39) {
      this.handlePager('NEXT');
    }
  }

  handleFilter = (e) => {
    const { fullComponentList } = this.state;
    const filter = e.target.value;

    this.setState({
      filter,
      componentList: this.filterList(fullComponentList, filter),
      indexCurrent: 0
    });
  }

  componentWillUnmount() {
    document.removeEventListener('keydown', this.handleKeyDown);
  }

  render() {
    const { classes, onClose } = this.props;
    const { componentList, indexCurrent, filter } = this.state;
    const entry = componentList[indexCurrent];
    // The widget's component code loads on demand here: for built-ins this is the
    // loadable from the registry; for custom widgets it's the iframe preview.
    const Preview = entry && entry.Component;
    const actions = [
      <Button key="select" variant="primary" onClick={() => this.handleSelect(entry)}>Select</Button>
    ];
    const prev = (
      <div
        className={classNames(classes.pager, classes.prev)}
        onClick={() => this.handlePager('PREV')}
      >
        <i className="material-icons">chevron_left</i>
      </div>
    );
    const next = (
      <div
        className={classNames(classes.pager, classes.next)}
        onClick={() => this.handlePager('NEXT')}
      >
        <i className="material-icons">chevron_right</i>
      </div>
    );

    const pageNumber = `${indexCurrent + 1} / ${componentList.length}`;

    return (
      <Modal
        title="Select Widget"
        actions={actions}
        onClose={onClose}
        className={classes.container}
      >
        <div className={classes.filter}>
          <input
            className={classes.filterInput}
            onChange={this.handleFilter}
            value={filter}
            placeholder="Filter Widgets"
          />
        </div>
        {entry && Preview &&
          <div className={classes.componentContainer}>
            {prev}
            <div className={classes.component} key={entry.schema.id}>
              <div className={classes.title}>
                <p>{entry.schema.title}</p>
                <p>{pageNumber}</p>
              </div>
              <div className={classNames(classes.componentWrapper, 'component-browser-wrapper')} data-gx-theme="light">
                <ErrorBoundary isEditor>
                  <Preview
                    {...entry.schema.defaultProps}
                  />
                </ErrorBoundary>
              </div>
            </div>
            {next}
          </div>
        }
      </Modal>
    );
  }
}

const styles = {
  container: {
    display: 'flex',
    flexFlow: 'column nowrap',
    position: 'relative'
  },
  componentContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    position: 'relative'
  },
  filter: {
    padding: '10px'
  },
  filterInput: {
    width: '100%',
    height: '30px',
    fontSize: '20px'
  },
  title: {
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: '10px'
  },
  component: {
    width: '50%',
    display: 'flex',
    justifyContent: 'center',
    padding: '15px',
    flexGrow: 2,
    alignItems: 'center',
    flexDirection: 'column'
  },
  componentWrapper: {
    flexGrow: 2,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
    width: '100%',
    height: '100%',
    minHeight: '350px',
    // The preview is light-scoped (data-gx-theme="light"), so this is the site's white, not the admin's dark.
    [DARK]: { background: A.surface }
  },
  pager: {
    display: 'flex',
    flex: '0 0 20px',
    paddingTop: '29px',
    '& > .material-icons': {
      fontSize: '40px',
      cursor: 'pointer',
    }
  }
};

export default injectSheet(styles)(ComponentBrowser);
